/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @fullcalendar components
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Custom styles for Calendar
import CalendarRoot from "examples/Calendar/CalendarRoot";

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from "context";

function Calendar({ header = {}, enhancedToolbar, toolbarAttribution, toolbarContext, ...rest }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const validClassNames = [
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
    "rahu-daytime",
    "rahu-nighttime",
  ];

  const events = rest.events
    ? rest.events.map((el) => ({
      ...el,
      className: validClassNames.find((item) => item === el.className)
        ? `event-${el.className}`
        : "event-info",
    }))
    : [];

  return (
    <Card
      sx={enhancedToolbar ? {
        height: "100%",
        position: "relative",
        overflow: "hidden",
        border: "0.5px solid #ced4da",
      } : { height: "100%" }}
    >
      <MDBox pt={header.title || header.date ? 2 : 0} px={2} lineHeight={1}>
        {header.title ? (
          <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
            {header.title}
          </MDTypography>
        ) : null}
        {header.date ? (
          <MDTypography component="p" variant="button" color="text" fontWeight="regular">
            {header.date}
          </MDTypography>
        ) : null}
      </MDBox>
      <CalendarRoot
        p={2}
        pt={enhancedToolbar ? 0 : 2}
        ownerState={{ darkMode }}
        sx={enhancedToolbar ? {
          "& .fc-header-toolbar": {
            backgroundColor: "#dbe7f2",
            border: "1px solid #c6d6e5",
            borderRadius: 0,
            marginBottom: toolbarAttribution
              ? { xs: "1.5rem !important", sm: "2.5rem !important" }
              : "1rem !important",
            marginLeft: "-1rem",
            marginRight: "-1rem",
            paddingLeft: "2rem",
            paddingRight: "2rem",
          },
          "& .fc-theme-standard td, & .fc-theme-standard th": {
            borderColor: "#cbd3da",
          },
          "& .fc .fc-col-header-cell-cushion": {
            color: "#495057",
            fontWeight: 600,
          },
          "& .fc .fc-daygrid-day-number": {
            color: "#344767",
            fontWeight: 500,
          },
        } : undefined}
      >
        <FullCalendar
          {...rest}
          plugins={[dayGridPlugin, listPlugin, timeGridPlugin, interactionPlugin]}
          events={events}
          height="100%"
        />
        {toolbarAttribution ? (
          <MDBox
            sx={{
              position: "absolute",
              top: { xs: "5rem", sm: "4rem" },
              left: "2rem",
              right: 2,
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 0, sm: 2 },
              pt: { xs: 0.5, sm: 0 },
              zIndex: 1,
            }}
          >
            {toolbarContext ? (
              <MDBox display="flex" alignItems="center" gap={0.5} mb={{ xs: 0.5, sm: 0 }} sx={{ color: "text.secondary", fontSize: "0.75rem", minWidth: 0 }}>
                <MDBox display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                  <MDBox component="img" src={toolbarContext.flagSrc} alt="" sx={{ width: 20, height: 15, objectFit: "cover", flexShrink: 0 }} />
                  <MDBox component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{toolbarContext.locationText}</MDBox>
                </MDBox>
              </MDBox>
            ) : null}
            <MDBox sx={{ color: "text.secondary", fontSize: "0.75rem", textAlign: "left", whiteSpace: "nowrap" }}>
              Powered by ලක් හඳහන | {" "}
              <MDBox
                component="a"
                href="https://app.lakhandahana.com/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "info.main", fontWeight: 600, textDecoration: "none" }}
              >
                app.lakhandahana.com
              </MDBox>
            </MDBox>
          </MDBox>
        ) : null}
      </CalendarRoot>
    </Card>
  );
}

// Setting default values for the props of Calendar
Calendar.defaultProps = {
  header: {
    title: "",
    date: "",
  },
  enhancedToolbar: false,
  toolbarAttribution: false,
  toolbarContext: null,
};

// Typechecking props for the Calendar
Calendar.propTypes = {
  header: PropTypes.shape({
    title: PropTypes.string,
    date: PropTypes.string,
  }),
  enhancedToolbar: PropTypes.bool,
  toolbarAttribution: PropTypes.bool,
  toolbarContext: PropTypes.shape({
    flagSrc: PropTypes.string.isRequired,
    locationText: PropTypes.string.isRequired,
  }),
};

export default Calendar;
